import { db } from '../config/firebase';
import { 
  doc, 
  updateDoc, 
  serverTimestamp, 
  collection, 
  addDoc, 
  getDoc,
  query,
  where,
  getDocs,
  writeBatch
} from 'firebase/firestore';

// Complete consultation from lawyer cases
export const completeConsultation = async (caseId, lawyerId) => {
  try {
    console.log('✅ Completing consultation for case:', caseId);
    
    // First, find the actual chat document for this case
    const chatsRef = collection(db, 'chats');
    const chatQuery = query(
      chatsRef,
      where('bookingId', '==', caseId)
    );
    const chatSnapshot = await getDocs(chatQuery);
    
    if (chatSnapshot.empty) {
      console.log('⚠️ No chat found for case:', caseId);
      // Try to find by clientId if bookingId doesn't work
      const bookingRef = doc(db, 'bookings', caseId);
      const bookingDoc = await getDoc(bookingRef);
      
      if (bookingDoc.exists()) {
        const bookingData = bookingDoc.data();
        const clientId = bookingData.clientId || bookingData.userId;
        
        if (clientId) {
          const clientChatQuery = query(
            chatsRef,
            where('clientId', '==', clientId),
            where('lawyerId', '==', lawyerId)
          );
          const clientChatSnapshot = await getDocs(clientChatQuery);
          
          if (!clientChatSnapshot.empty) {
            const chatDoc = clientChatSnapshot.docs[0];
            const chatId = chatDoc.id;
            
            // Get current chat data to preserve client information
            const currentChatData = chatDoc.data();
            
            // Update chat status while preserving client profile data
            await updateDoc(doc(db, 'chats', chatId), {
              status: 'completed',
              // Preserve client profile information
              clientName: currentChatData.clientName || 'Client',
              clientPhoto: currentChatData.clientPhoto || '/default-client.png',
              // Preserve other important fields
              caseType: currentChatData.caseType || 'Legal Consultation',
              updatedAt: serverTimestamp()
            });
            
            // Update booking status
            await updateDoc(bookingRef, {
              status: 'completed',
              consultationCompleted: true,
              completedAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            });
            
            // Send automated completion message
            await sendAutomatedMessage(chatId, 'system', '✅ Consultation completed successfully! This chat is now read-only.');
            
            console.log('✅ Consultation completed successfully via clientId lookup');
            return { success: true };
          }
        }
      }
      
      throw new Error('No chat found for this case');
    }
    
    // Update chat status
    const chatDoc = chatSnapshot.docs[0];
    const chatId = chatDoc.id;
    const currentChatData = chatDoc.data();
    
    await updateDoc(doc(db, 'chats', chatId), {
      status: 'completed',
      // Preserve client profile information
      clientName: currentChatData.clientName || 'Client',
      clientPhoto: currentChatData.clientPhoto || '/default-client.png',
      // Preserve other important fields
      caseType: currentChatData.caseType || 'Legal Consultation',
      updatedAt: serverTimestamp()
    });
    
    // Update booking status
    const bookingRef = doc(db, 'bookings', caseId);
    await updateDoc(bookingRef, {
      status: 'completed',
      consultationCompleted: true,
      completedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    // Send automated completion message
    await sendAutomatedMessage(chatId, 'system', '✅ Consultation completed successfully! This chat is now read-only.');
    
    console.log('✅ Consultation completed successfully');
    return { success: true };
  } catch (error) {
    console.error('❌ Error completing consultation:', error);
    return { success: false, error: error.message };
  }
};

// End consultation from chat
export const endConsultation = async (chatId, endedBy, endedByType) => {
  try {
    console.log('🔚 Ending consultation for chat:', chatId, 'by:', endedByType, endedBy);
    
    // Get chat data
    const chatRef = doc(db, 'chats', chatId);
    const chatDoc = await getDoc(chatRef);
    
    if (!chatDoc.exists()) {
      throw new Error('Chat not found');
    }
    
    const chatData = chatDoc.data();
    
                // Update chat status while preserving client profile data
            await updateDoc(chatRef, {
              status: 'completed',
              endedBy: endedBy,
              endedByType: endedByType, // 'lawyer' or 'client'
              endedAt: serverTimestamp(),
              // Preserve client profile information
              clientName: chatData.clientName || 'Client',
              clientPhoto: chatData.clientPhoto || '/default-client.png',
              // Preserve other important fields
              caseType: chatData.caseType || 'Legal Consultation',
              updatedAt: serverTimestamp()
            });
    
    // Update booking status if exists
    if (chatData.bookingId) {
      const bookingRef = doc(db, 'bookings', chatData.bookingId);
      await updateDoc(bookingRef, {
        status: 'completed',
        consultationCompleted: true,
        endedBy: endedBy,
        endedByType: endedByType,
        endedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    }
    
    // Send automated ending message
    const endMessage = endedByType === 'lawyer' 
      ? '🔚 Consultation ended by lawyer. This chat is now read-only.'
      : '🔚 Consultation ended by client. This chat is now read-only.';
    
    await sendAutomatedMessage(chatId, 'system', endMessage);
    
    console.log('✅ Consultation ended successfully');
    return { success: true };
  } catch (error) {
    console.error('❌ Error ending consultation:', error);
    return { success: false, error: error.message };
  }
};

// Submit review for completed consultation
export const submitReview = async (caseId, clientId, lawyerId, reviewData) => {
  try {
    console.log('⭐ Submitting review for case:', caseId);
    
    const review = {
      caseId: caseId,
      clientId: clientId,
      lawyerId: lawyerId,
      rating: reviewData.rating,
      comment: reviewData.comment,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    // Add review to reviews collection
    const reviewRef = await addDoc(collection(db, 'reviews'), review);
    
    // Update lawyer's average rating
    await updateLawyerRating(lawyerId);
    
    // Update booking with review status
    const bookingRef = doc(db, 'bookings', caseId);
    await updateDoc(bookingRef, {
      reviewSubmitted: true,
      reviewId: reviewRef.id,
      updatedAt: serverTimestamp()
    });
    
    console.log('✅ Review submitted successfully');
    return { success: true, reviewId: reviewRef.id };
  } catch (error) {
    console.error('❌ Error submitting review:', error);
    return { success: false, error: error.message };
  }
};

// Update lawyer's average rating
const updateLawyerRating = async (lawyerId) => {
  try {
    // Get all reviews for this lawyer
    const reviewsQuery = query(
      collection(db, 'reviews'),
      where('lawyerId', '==', lawyerId)
    );
    
    const reviewsSnapshot = await getDocs(reviewsQuery);
    
    if (!reviewsSnapshot.empty) {
      let totalRating = 0;
      let reviewCount = 0;
      
      reviewsSnapshot.forEach(doc => {
        const review = doc.data();
        if (review.rating) {
          totalRating += review.rating;
          reviewCount++;
        }
      });
      
      if (reviewCount > 0) {
        const averageRating = totalRating / reviewCount;
        
        // Update lawyer profile
        const lawyerRef = doc(db, 'lawyers', lawyerId);
        await updateDoc(lawyerRef, {
          averageRating: averageRating,
          reviewCount: reviewCount,
          updatedAt: serverTimestamp()
        });
        
        console.log('✅ Lawyer rating updated:', averageRating, 'from', reviewCount, 'reviews');
      }
    }
  } catch (error) {
    console.error('❌ Error updating lawyer rating:', error);
  }
};

// Send automated message to chat
const sendAutomatedMessage = async (chatId, type, content) => {
  try {
    console.log('📨 Sending automated message to chat:', chatId, 'Content:', content);
    
    const messageData = {
      content: content,
      type: type, // 'system', 'user', 'lawyer'
      senderId: 'system',
      senderType: 'system',
      createdAt: serverTimestamp(),
      isAutomated: true
    };
    
    // Add message to chat subcollection
    const messagesRef = collection(db, 'chats', chatId, 'messages');
    const messageRef = await addDoc(messagesRef, messageData);
    console.log('✅ Message added to subcollection:', messageRef.id);
    
    // Update chat's last message
    const chatRef = doc(db, 'chats', chatId);
    await updateDoc(chatRef, {
      lastMessage: content,
      lastMessageTime: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    console.log('✅ Chat document updated with automated message');
    console.log('✅ Automated message sent successfully:', content);
  } catch (error) {
    console.error('❌ Error sending automated message:', error);
    throw error; // Re-throw to handle in calling function
  }
};

// Check if consultation can be ended
export const canEndConsultation = (chatData) => {
  return chatData.status === 'active' || chatData.status === 'pending';
};

// Check if consultation is completed
export const isConsultationCompleted = (chatData) => {
  return chatData.status === 'completed';
};

// Get consultation status for display
export const getConsultationStatus = (chatData) => {
  switch (chatData.status) {
    case 'pending':
      return { text: 'Pending', color: 'bg-yellow-500', textColor: 'text-yellow-700' };
    case 'active':
      return { text: 'Active', color: 'bg-green-500', textColor: 'text-green-700' };
    case 'completed':
      return { text: 'Completed', color: 'bg-gray-500', textColor: 'text-gray-700' };
    default:
      return { text: 'Unknown', color: 'bg-gray-500', textColor: 'text-gray-700' };
  }
};
